package com.qrkontaq.data.repository

import com.qrkontaq.core.auth.Session
import com.qrkontaq.core.config.AppConfig
import com.qrkontaq.data.dto.AdminMenuDto
import com.qrkontaq.data.dto.AdminMenuItemDto
import com.qrkontaq.data.dto.AdminRestaurantDto
import com.qrkontaq.data.dto.AutoRenewRequestDto
import com.qrkontaq.data.dto.AvailabilityRequestDto
import com.qrkontaq.data.dto.CheckoutRequestDto
import com.qrkontaq.data.dto.CheckoutResponseDto
import com.qrkontaq.data.dto.CloseTableLoyaltyDto
import com.qrkontaq.data.dto.CloseTableRequestDto
import com.qrkontaq.data.dto.CloseTableResponseDto
import com.qrkontaq.data.dto.CreateCategoryRequestDto
import com.qrkontaq.data.dto.CreateInviteRequestDto
import com.qrkontaq.data.dto.CreateItemRequestDto
import com.qrkontaq.data.dto.CreateRestaurantRequestDto
import com.qrkontaq.data.dto.CreateTableRequestDto
import com.qrkontaq.data.dto.AcceptInviteResponseDto
import com.qrkontaq.data.dto.InviteDto
import com.qrkontaq.data.dto.LoyaltyCandidateDto
import com.qrkontaq.data.dto.LoyaltyCandidatesDto
import com.qrkontaq.data.dto.LoyaltyGuestDto
import com.qrkontaq.data.dto.LoyaltyIdentifyRequestDto
import com.qrkontaq.data.dto.MediaUploadResponseDto
import com.qrkontaq.data.dto.MenuCategoryDto
import com.qrkontaq.data.dto.MyRestaurantDto
import com.qrkontaq.data.dto.MyStatsDto
import com.qrkontaq.data.dto.OrderEntryDto
import com.qrkontaq.data.dto.ReorderRequestDto
import com.qrkontaq.data.dto.StaffDto
import com.qrkontaq.data.dto.CreateOrderRequestDto
import com.qrkontaq.data.dto.OrderDto
import com.qrkontaq.data.dto.StatsDto
import com.qrkontaq.data.dto.UpdateOrderStatusRequestDto
import com.qrkontaq.data.dto.SubscriptionInfoDto
import com.qrkontaq.data.dto.TableDto
import com.qrkontaq.data.dto.TableLayoutRequestDto
import com.qrkontaq.data.dto.TablePositionDto
import com.qrkontaq.data.dto.UpdateCategoryRequestDto
import com.qrkontaq.data.dto.UpdateItemRequestDto
import com.qrkontaq.data.dto.UpdateRestaurantRequestDto
import com.qrkontaq.data.dto.WaiterCallDto
import com.qrkontaq.data.http.ApiResponse
import com.qrkontaq.data.http.apiDelete
import com.qrkontaq.data.http.apiGet
import com.qrkontaq.data.http.apiPost
import com.qrkontaq.data.http.apiPut
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.header
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.w3c.fetch.RequestInit
import org.w3c.files.File
import org.w3c.xhr.FormData
import kotlin.js.json

class AdminRepository(
    private val client: HttpClient,
) {
    // --- Restaurants ---

    suspend fun myRestaurants(): List<MyRestaurantDto> =
        client.apiGet<List<MyRestaurantDto>>("v1/me/restaurants").data.orEmpty()

    /** Full envelope so the boot flow can tell an auth failure from a flaky network. */
    suspend fun myRestaurantsResponse(): ApiResponse<List<MyRestaurantDto>> =
        client.apiGet("v1/me/restaurants")

    suspend fun createRestaurant(request: CreateRestaurantRequestDto): ApiResponse<AdminRestaurantDto> =
        client.apiPost("v1/admin/restaurants", request)

    suspend fun getRestaurant(rid: String): AdminRestaurantDto? =
        client.apiGet<AdminRestaurantDto>("v1/admin/restaurants/$rid").data

    suspend fun updateRestaurant(
        rid: String,
        request: UpdateRestaurantRequestDto,
    ): AdminRestaurantDto? = client.apiPatch("v1/admin/restaurants/$rid", request)

    /**
     * Permanently deletes a venue and everything tied to it — menu, media (logo/cover/dish
     * photos), tables & QR codes, orders, staff/invites, loyalty and the site address/domain.
     * Owner-only on the backend; the UI gates it to the owner and a typed-name confirmation.
     */
    suspend fun deleteRestaurant(rid: String): ApiResponse<Unit> =
        client.apiDelete("v1/admin/restaurants/$rid")

    /**
     * Permanently deletes the signed-in account together with every venue it owns, freeing the
     * email for a fresh sign-up. Used when the owner removes their last venue.
     */
    suspend fun deleteAccount(): ApiResponse<Unit> =
        client.apiDelete("v1/users/me")

    // --- Menu ---

    suspend fun getMenu(rid: String): AdminMenuDto? =
        client.apiGet<AdminMenuDto>("v1/admin/restaurants/$rid/menu").data

    suspend fun createCategory(
        rid: String,
        name: Map<String, String>,
        isBar: Boolean = false,
    ): ApiResponse<MenuCategoryDto> =
        client.apiPost("v1/admin/restaurants/$rid/categories", CreateCategoryRequestDto(name, isBar))

    suspend fun updateCategory(
        rid: String,
        categoryId: String,
        request: UpdateCategoryRequestDto,
    ): MenuCategoryDto? = client.apiPatch("v1/admin/restaurants/$rid/categories/$categoryId", request)

    suspend fun deleteCategory(
        rid: String,
        categoryId: String,
        force: Boolean,
    ): ApiResponse<Unit> =
        client.apiDelete("v1/admin/restaurants/$rid/categories/$categoryId?force=$force")

    suspend fun reorderCategories(
        rid: String,
        order: List<OrderEntryDto>,
    ): ApiResponse<Unit> =
        client.apiPut("v1/admin/restaurants/$rid/categories/order", ReorderRequestDto(order))

    suspend fun createItem(
        rid: String,
        request: CreateItemRequestDto,
    ): ApiResponse<AdminMenuItemDto> = client.apiPost("v1/admin/restaurants/$rid/items", request)

    suspend fun updateItem(
        rid: String,
        itemId: String,
        request: UpdateItemRequestDto,
    ): AdminMenuItemDto? = client.apiPatch("v1/admin/restaurants/$rid/items/$itemId", request)

    suspend fun deleteItem(
        rid: String,
        itemId: String,
    ): ApiResponse<Unit> = client.apiDelete("v1/admin/restaurants/$rid/items/$itemId")

    suspend fun reorderItems(
        rid: String,
        order: List<OrderEntryDto>,
    ): ApiResponse<Unit> =
        client.apiPut("v1/admin/restaurants/$rid/items/order", ReorderRequestDto(order))

    suspend fun setAvailability(
        rid: String,
        itemId: String,
        isAvailable: Boolean,
    ): AdminMenuItemDto? =
        client.apiPatch(
            "v1/admin/restaurants/$rid/items/$itemId/availability",
            AvailabilityRequestDto(isAvailable),
        )

    // --- Tables ---

    suspend fun tables(rid: String): List<TableDto> =
        client.apiGet<List<TableDto>>("v1/admin/restaurants/$rid/tables").data.orEmpty()

    suspend fun createTable(
        rid: String,
        label: String,
    ): ApiResponse<TableDto> =
        client.apiPost("v1/admin/restaurants/$rid/tables", CreateTableRequestDto(label))

    suspend fun deleteTable(
        rid: String,
        tableId: String,
    ): ApiResponse<Unit> = client.apiDelete("v1/admin/restaurants/$rid/tables/$tableId")

    /** Persists the hall-plan positions so the layout is shared across devices. */
    suspend fun saveTableLayout(
        rid: String,
        positions: List<TablePositionDto>,
    ): ApiResponse<Unit> =
        client.apiPut(
            "v1/admin/restaurants/$rid/tables/layout",
            TableLayoutRequestDto(positions),
        )

    /** Fetches the QR PDF with auth and triggers a browser download. */
    suspend fun downloadQrPdf(
        rid: String,
        categorySlug: String? = null,
    ) {
        val query = categorySlug?.let { "?categorySlug=$it" }.orEmpty()
        val url = AppConfig.getApiUrl("v1/admin/restaurants/$rid/tables/qr.pdf$query")
        val response =
            window.fetch(
                url,
                RequestInit(
                    headers = json("Authorization" to "Bearer ${Session.accessToken.orEmpty()}"),
                ),
            ).await()
        val blob = response.blob().await()
        val objectUrl = js("URL.createObjectURL(blob)") as String
        val anchor = kotlinx.browser.document.createElement("a")
        anchor.setAttribute("href", objectUrl)
        anchor.setAttribute("download", "qr-codes.pdf")
        anchor.asDynamic().click()
        js("URL.revokeObjectURL(objectUrl)")
    }

    // --- Staff ---

    suspend fun staff(rid: String): StaffDto? =
        client.apiGet<StaffDto>("v1/admin/restaurants/$rid/staff").data

    /** Joins the signed-in user to a restaurant via an invite code. */
    suspend fun acceptInvite(code: String): AcceptInviteResponseDto? =
        client.apiPost<AcceptInviteResponseDto, Unit>("v1/admin/invites/$code/accept", Unit).data

    suspend fun createInvite(
        rid: String,
        role: String,
    ): ApiResponse<InviteDto> =
        client.apiPost("v1/admin/restaurants/$rid/staff/invites", CreateInviteRequestDto(role))

    suspend fun deleteInvite(
        rid: String,
        inviteId: String,
    ): ApiResponse<Unit> = client.apiDelete("v1/admin/restaurants/$rid/staff/invites/$inviteId")

    suspend fun removeMember(
        rid: String,
        userId: String,
    ): ApiResponse<Unit> = client.apiDelete("v1/admin/restaurants/$rid/staff/$userId")

    // --- Subscription / stats ---

    suspend fun subscription(rid: String): SubscriptionInfoDto? =
        client.apiGet<SubscriptionInfoDto>("v1/admin/restaurants/$rid/subscription").data

    suspend fun checkout(
        rid: String,
        plan: String,
        autoRenew: Boolean = false,
        lang: String = "uk",
    ): CheckoutResponseDto? =
        client.apiPost<CheckoutResponseDto, CheckoutRequestDto>(
            "v1/admin/restaurants/$rid/subscription/checkout",
            CheckoutRequestDto(plan, autoRenew, lang),
        ).data

    suspend fun setAutoRenew(
        rid: String,
        enabled: Boolean,
    ): ApiResponse<Unit> =
        client.apiPost(
            "v1/admin/restaurants/$rid/subscription/auto-renew",
            AutoRenewRequestDto(enabled),
        )

    suspend fun stats(rid: String, from: String? = null, to: String? = null): StatsDto? =
        client.apiGet<StatsDto>(
            "v1/admin/restaurants/$rid/stats" +
                if (from != null && to != null) "?from=$from&to=$to" else "",
        ).data

    /** Personal overview of the logged-in staff member: what they cooked / served. */
    suspend fun myStats(rid: String, from: String? = null, to: String? = null): MyStatsDto? =
        client.apiGet<MyStatsDto>(
            "v1/admin/restaurants/$rid/stats/my" +
                if (from != null && to != null) "?from=$from&to=$to" else "",
        ).data

    // --- Waiter calls ---

    suspend fun waiterCalls(
        rid: String,
        status: String? = null,
    ): List<WaiterCallDto> {
        val query = status?.let { "?status=$it" }.orEmpty()
        return client.apiGet<List<WaiterCallDto>>("v1/admin/restaurants/$rid/waiter-calls$query").data.orEmpty()
    }

    suspend fun resolveWaiterCall(
        rid: String,
        callId: String,
    ): ApiResponse<Unit> =
        client.apiPost("v1/admin/restaurants/$rid/waiter-calls/$callId/resolve", Unit)

    // --- Orders ---

    suspend fun orders(rid: String): List<OrderDto> =
        client.apiGet<List<OrderDto>>("v1/admin/restaurants/$rid/orders").data.orEmpty()

    suspend fun createOrder(
        rid: String,
        request: CreateOrderRequestDto,
    ): OrderDto? =
        client.apiPost<OrderDto, CreateOrderRequestDto>(
            "v1/admin/restaurants/$rid/orders",
            request,
        ).data

    suspend fun setOrderStatus(
        rid: String,
        orderId: String,
        status: String,
    ): OrderDto? =
        client.apiPost<OrderDto, UpdateOrderStatusRequestDto>(
            "v1/admin/restaurants/$rid/orders/$orderId/status",
            UpdateOrderStatusRequestDto(status),
        ).data

    suspend fun deleteOrder(
        rid: String,
        orderId: String,
    ): ApiResponse<Unit> = client.apiDelete("v1/admin/restaurants/$rid/orders/$orderId")

    /**
     * Closes (marks paid) all orders of a table, optionally attaching a guest loyalty
     * code and a redeem amount. Loyalty codes are permanent now; the 409
     * LOYALTY_CODE_* branch remains only as a fallback against older backends.
     */
    suspend fun closeTableOrders(
        rid: String,
        tableId: String,
        loyaltyCode: String? = null,
        redeemMinor: Int? = null,
    ): CloseTableResult {
        val response =
            client.post(AppConfig.getApiUrl("v1/admin/restaurants/$rid/orders/tables/$tableId/close")) {
                contentType(ContentType.Application.Json)
                setBody(CloseTableRequestDto(loyaltyCode = loyaltyCode, redeemMinor = redeemMinor))
            }
        if (response.status.value == 409) {
            val text = runCatching { response.bodyAsText() }.getOrDefault("")
            // Both EXPIRED and USED mean the same thing to the waiter: ask the guest for a fresh code.
            return if ("LOYALTY_CODE_" in text) {
                CloseTableResult.LoyaltyCodeExpired
            } else {
                CloseTableResult.Failed
            }
        }
        if (response.status.value !in 200..299) return CloseTableResult.Failed
        val loyalty =
            runCatching { response.body<ApiResponse<CloseTableResponseDto>>().data?.loyalty }.getOrNull()
        return CloseTableResult.Closed(loyalty)
    }

    // --- Loyalty ---

    /** Loyalty guests who recently scanned the QR of this table (a display-only hint). */
    suspend fun loyaltyCandidates(
        rid: String,
        tableId: String,
    ): List<LoyaltyCandidateDto> =
        client.apiGet<LoyaltyCandidatesDto>(
            "v1/admin/restaurants/$rid/loyalty/candidates?tableId=$tableId",
        ).data?.candidates.orEmpty()

    /** Resolves a guest's 6-digit loyalty code into their name and balance. */
    suspend fun identifyLoyalty(
        rid: String,
        code: String,
    ): LoyaltyIdentifyResult {
        val response =
            client.post(AppConfig.getApiUrl("v1/admin/restaurants/$rid/loyalty/identify")) {
                contentType(ContentType.Application.Json)
                setBody(LoyaltyIdentifyRequestDto(code))
            }
        return when {
            response.status.value == 404 -> LoyaltyIdentifyResult.NotFound
            response.status.value == 410 -> LoyaltyIdentifyResult.Expired
            response.status.value !in 200..299 -> LoyaltyIdentifyResult.Error
            else ->
                runCatching { response.body<ApiResponse<LoyaltyGuestDto>>().data }.getOrNull()
                    ?.let { LoyaltyIdentifyResult.Found(it) }
                    ?: LoyaltyIdentifyResult.Error
        }
    }

    // --- Media ---

    suspend fun uploadMedia(
        rid: String,
        kind: String,
        file: File,
    ): String? {
        val formData = FormData()
        formData.append("file", file)
        val response =
            window.fetch(
                AppConfig.getApiUrl("v1/admin/restaurants/$rid/media?kind=$kind"),
                RequestInit(
                    method = "POST",
                    headers = json("Authorization" to "Bearer ${Session.accessToken.orEmpty()}"),
                    body = formData,
                ),
            ).await()
        if (!response.ok) return null
        val text = response.text().await()
        return runCatching {
            kotlinx.serialization.json.Json { ignoreUnknownKeys = true }
                .decodeFromString<ApiResponse<MediaUploadResponseDto>>(text)
                .data?.url
        }.getOrNull()
    }
}

/** Outcome of resolving a loyalty code (identify endpoint). */
sealed interface LoyaltyIdentifyResult {
    data class Found(val guest: LoyaltyGuestDto) : LoyaltyIdentifyResult

    data object NotFound : LoyaltyIdentifyResult

    data object Expired : LoyaltyIdentifyResult

    data object Error : LoyaltyIdentifyResult
}

/** Outcome of closing a table's orders (optionally with loyalty). */
sealed interface CloseTableResult {
    data class Closed(val loyalty: CloseTableLoyaltyDto?) : CloseTableResult

    data object LoyaltyCodeExpired : CloseTableResult

    data object Failed : CloseTableResult
}

/** PATCH helper matching the project's apiGet/apiPost style. */
private suspend inline fun <reified T, reified R> HttpClient.apiPatch(
    path: String,
    body: R,
): T? =
    patch(AppConfig.getApiUrl(path)) {
        contentType(ContentType.Application.Json)
        setBody(body)
    }.body<ApiResponse<T>>().data
